import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { GridColumnResizeParams } from '@mui/x-data-grid/models/params/gridColumnResizeParams';
import { DataGrid } from '@mui/x-data-grid/DataGrid';
import { DataGridProps } from '@mui/x-data-grid/';
import { useColumnOrder } from '../../hooks/useColumnOrder';
import ColumnSettingsDialog from './ColumnSettingsDialog';
import CustomColumnMenu from './CustomColumnMenu';
import { GridColumnMenuProps } from '@mui/x-data-grid/components';

interface ColumnMenuWrapperProps extends GridColumnMenuProps {
  onConfigureColumns: () => void;
}

const ColumnMenuWrapper: React.FC<ColumnMenuWrapperProps> = ({ onConfigureColumns, ...props }) => (
  <CustomColumnMenu 
    {...props} 
    onConfigureColumns={onConfigureColumns} 
  />
);

type ColumnWidthModel = Record<string, number>;

const getWidthStorageKey = (storageKey: string): string => `${storageKey}-widths`;

const parseColumnWidths = (raw: string | null): ColumnWidthModel => {
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    return Object.entries(parsed).reduce<ColumnWidthModel>((acc, [field, width]) => {
      if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
        acc[field] = width;
      }
      return acc;
    }, {});
  } catch (error) {
    console.error('Failed to parse saved column widths:', error);
    return {};
  }
};

const applySavedWidths = (columns: GridColDef[], columnWidths: ColumnWidthModel): GridColDef[] =>
  columns.map(column => {
    const savedWidth = columnWidths[column.field];

    if (savedWidth === undefined) {
      return column;
    }

    return {
      ...column,
      width: savedWidth,
      flex: undefined,
    };
  });

interface DataGridContainerProps extends Omit<DataGridProps, 'rows' | 'columns'> {
  rows: readonly unknown[];
  columns: GridColDef[];
  loading?: boolean;
  error?: string | null;
  selectedRowId?: string | null;
  onRowSelectionChange?: (selectionModel: GridRowSelectionModel) => void;
  selectedItemInfo?: string;
  actionButtons?: React.ReactNode;
  filterSection?: React.ReactNode;
  // Performance optimization
  resetSelectionOnDataChange?: boolean;
  // Pagination props
  paginationMode?: 'client' | 'server';
  paginationModel?: { page: number; pageSize: number };
  rowCount?: number;
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
  pageSizeOptions?: number[];
  // Height constraint options
  maxHeight?: string | number;
  minHeight?: string | number;
  // Layout control - use flex to fill available space without scrollbar
  useFlexLayout?: boolean;
  // Column ordering
  columnOrderStorageKey?: string; // Unique key for storing column order (e.g., 'quotations', 'payments')
  enableColumnOrdering?: boolean; // Enable/disable column ordering feature
  onDisplayColumnsChange?: (columns: GridColDef[], visibilityModel: Record<string, boolean>) => void;
}

const DataGridContainer: React.FC<DataGridContainerProps> = ({
  rows,
  columns,
  loading = false,
  error: _error = null,
  selectedRowId = null,
  onRowSelectionChange,
  selectedItemInfo,
  actionButtons,
  filterSection,
  resetSelectionOnDataChange = false,
  paginationMode = 'server',
  paginationModel = { page: 0, pageSize: 10 },
  rowCount = 0,
  onPaginationModelChange,
  pageSizeOptions = [5, 10, 25, 50, 100],
  maxHeight = '70vh', // Limit height to prevent overflow
  useFlexLayout = true, // Default to flex layout for better responsive behavior
  columnOrderStorageKey,
  enableColumnOrdering = true, // Enable by default
  onDisplayColumnsChange,
  getRowId,
  ...dataGridProps
}) => {
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  // Extract and ignore columnVisibilityModel props if column ordering is enabled
  const { 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    columnVisibilityModel: _ignoredVisibilityModel,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onColumnVisibilityModelChange: _ignoredVisibilityHandler,
    onColumnWidthChange: externalOnColumnWidthChange,
    ...restDataGridProps 
  } = dataGridProps;

  // Use column ordering hook if enabled and storage key is provided
  const storageKey = columnOrderStorageKey || 'datagrid-columns-default';
  const widthStorageKey = useMemo(() => getWidthStorageKey(storageKey), [storageKey]);
  const {
    orderedColumns,
    columnOrder,
    columnVisibilityModel: customColumnVisibilityModel,
    moveColumnUp,
    moveColumnDown,
    toggleColumnVisibility,
    resetToDefault,
    getColumnLabel,
  } = useColumnOrder({
    columns,
    storageKey,
    initialVisibility: dataGridProps.columnVisibilityModel as Record<string, boolean> | undefined,
  });

  const [columnWidths, setColumnWidths] = useState<ColumnWidthModel>(() =>
    parseColumnWidths(localStorage.getItem(widthStorageKey)),
  );

  useEffect(() => {
    setColumnWidths(parseColumnWidths(localStorage.getItem(widthStorageKey)));
  }, [widthStorageKey]);

  useEffect(() => {
    localStorage.setItem(widthStorageKey, JSON.stringify(columnWidths));
  }, [columnWidths, widthStorageKey]);

  const handleColumnWidthChange = useCallback<NonNullable<DataGridProps['onColumnWidthChange']>>(
    (params, event, details) => {
      const resizeParams = params as GridColumnResizeParams;
      const field = resizeParams.colDef.field;
      const width = resizeParams.width;

      if (field && typeof width === 'number' && Number.isFinite(width) && width > 0) {
        setColumnWidths(prev => (prev[field] === width ? prev : { ...prev, [field]: width }));
      }

      externalOnColumnWidthChange?.(params, event, details);
    },
    [externalOnColumnWidthChange],
  );

  // Use ordered columns if feature is enabled, otherwise use original columns.
  const baseColumns = enableColumnOrdering ? orderedColumns : columns;

  // Reapply user-resized widths after rerenderss so widths don'ot reset.
  const finalColumns = useMemo(() => applySavedWidths(baseColumns, columnWidths), [baseColumns, columnWidths]);
  
  // Use custom visibility model if column ordering is enabled, otherwise use the passed one
  const finalColumnVisibilityModel = enableColumnOrdering 
    ? customColumnVisibilityModel 
    : dataGridProps.columnVisibilityModel;

  // Notify parent of live display column state (order + visibility)
  useEffect(() => {
    if (enableColumnOrdering && onDisplayColumnsChange) {
      onDisplayColumnsChange(orderedColumns, customColumnVisibilityModel);
    }
  }, [enableColumnOrdering, onDisplayColumnsChange, orderedColumns, customColumnVisibilityModel]);

  // Auto-reset selection when data changes if enabled
  useEffect(() => {
    if (resetSelectionOnDataChange && onRowSelectionChange && selectedRowId) {
      onRowSelectionChange([]);
    }
  }, [rows, resetSelectionOnDataChange, onRowSelectionChange, selectedRowId]);

  return (
    <>
      {filterSection && (
        <Paper sx={{ p: 2, mb: 2 }}>
          {filterSection}
        </Paper>
      )}

      {/* Action Buttons Row - only render if there's content */}
      {(actionButtons || selectedItemInfo) && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center', 
          mb: 2, 
          justifyContent: 'flex-start',
          flexWrap: 'wrap'
        }}>
          {actionButtons}
          {selectedItemInfo && (
            <Typography variant="body2" color="textSecondary">
              {selectedItemInfo}
            </Typography>
          )}
        </Box>
      )}

      {/* DataGrid Container - uses available space efficiently */}
      <Paper sx={{ 
        p: 2, 
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        height: useFlexLayout ? 'auto' : maxHeight
      }}>
        <DataGrid
          rows={rows}
          columns={finalColumns}
          loading={loading}
          paginationMode={paginationMode}
          paginationModel={paginationModel}
          rowCount={rowCount}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={pageSizeOptions}
          checkboxSelection={false}
          disableRowSelectionOnClick={false}
          onRowSelectionModelChange={onRowSelectionChange}
          rowSelectionModel={selectedRowId ? [selectedRowId] : []}
          getRowId={getRowId}
          disableColumnFilter
          disableColumnSorting
          disableColumnMenu={!enableColumnOrdering}
          autoHeight={useFlexLayout}
          columnVisibilityModel={finalColumnVisibilityModel}
          onColumnWidthChange={handleColumnWidthChange}
          slots={
            enableColumnOrdering
              ? { 
                  columnMenu: (props) => (
                    <ColumnMenuWrapper 
                      {...props} 
                      onConfigureColumns={() => setSettingsDialogOpen(true)} 
                    />
                  )
                }
              : undefined
          }
          sx={{
            ...(useFlexLayout ? {} : { height: '100%' }),
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider'
            },
            // Ensure DataGrid scrolls properly
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto'
            }
          }}
          {...restDataGridProps}
        />
      </Paper>

      {/* Column Settings Dialog */}
      {enableColumnOrdering && (
        <ColumnSettingsDialog
          open={settingsDialogOpen}
          onClose={() => setSettingsDialogOpen(false)}
          columnOrder={columnOrder}
          getColumnLabel={getColumnLabel}
          onMoveUp={moveColumnUp}
          onMoveDown={moveColumnDown}
          onToggleVisibility={toggleColumnVisibility}
          onReset={resetToDefault}
        />
      )}
    </>
  );
};

export default DataGridContainer;
